import { alertListQuerySchema, alertRuleListQuerySchema, type AlertListQuery, type AlertRuleListQuery, type Page } from '@chukua/common';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { toListParams, type ListQuerySchema } from '../../../shared/policies/list-params';
import { useListQuery, type BaseListQuery } from '../../../shared/hooks/use-list-query';
import { listAlertRules, listAlerts, type AlertRuleView, type AlertView } from '../alerts.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type AlertListState<T> = { status: 'loading' } | { status: 'loaded'; page: Page<T>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

function usePagedList<Q extends BaseListQuery, T>(schema: ListQuerySchema<Q>, fetchPage: (query: Q) => Promise<Page<T>>) {
  const list = useListQuery(schema);
  const { query } = list;
  const [state, setState] = useState<AlertListState<T>>({ status: 'loading' });
  const keyOf = toListParams(query).toString();
  // Only the newest request may write state: a slower answer for an older query (or a pushed refetch begun before a filter changed) would
  // otherwise land last and leave the old filter's rows on screen. The ref also gives reload/refresh the query as of now, not as of the render that made them.
  const latestRequest = useRef(0);
  const current = useRef({ query, keyOf });
  // Declared before the fetching effect so the ref holds the new query by the time that effect reads it.
  useEffect(() => {
    current.current = { query, keyOf };
  }, [query, keyOf]);

  /** Fetches the current query; `keepRowsOnError` is for background refetches, which must not replace shown rows with an error. */
  const load = useCallback(
    async (keepRowsOnError: boolean): Promise<void> => {
      const request = ++latestRequest.current;
      const { query: asked, keyOf: askedKey } = current.current;
      try {
        const page = await fetchPage(asked);
        if (request === latestRequest.current) setState({ status: 'loaded', page, queryKey: askedKey });
      } catch (err) {
        if (request !== latestRequest.current || keepRowsOnError) return;
        setState({ status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' });
      }
    },
    [fetchPage],
  );

  useEffect(() => {
    void load(false);
    return () => {
      latestRequest.current += 1;
    };
  }, [keyOf, load]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf;

  /** Re-fetches the current page after a mutation commits. */
  const reload = () => load(false);

  /** A background refetch (a change pushed by the server): a failure keeps the rows already shown; the next push or reload tries again. */
  const refresh = () => load(true);

  return { state, list, isRefreshing, reload, refresh };
}

/** The page of raised alerts the URL's list query asks for. */
export function useRaisedAlertList() {
  return usePagedList<AlertListQuery, AlertView>(alertListQuerySchema, listAlerts);
}

/** The page of alert rules the URL's list query asks for. */
export function useAlertRuleList() {
  return usePagedList<AlertRuleListQuery, AlertRuleView>(alertRuleListQuerySchema, listAlertRules);
}
